import { z } from 'zod';
import { CORE_ROUTING_POLICY_IDS } from '../../../core/connectors/routing';
import { connectorCommonProps, type ConnectorManifest, type ConnectorPack } from '../../../lib/diagram/connector-manifest.types';
import { finalizeConnectorExportWithLabel } from '../../../lib/diagram/connector-label-chip';
import { registerConnector } from '../../../lib/diagram/registries';
import { registerDomainProfile } from '../../../core/data-profiles';
import { ProcessProfile } from '../ProcessProfile';

/**
 * Props schema for sequence flow connector
 * Extends common connector props with flowchart-specific defaults
 */
const sequenceFlowProps = connectorCommonProps.extend({
  routingType: z.enum(['straight', 'orthogonal', 'orthogonalRounded', 'bezier']).default('orthogonalRounded'),
  label: z.string().optional(),
  fromAnchor: z.enum(['top', 'right', 'bottom', 'left']).optional(),
  toAnchor: z.enum(['top', 'right', 'bottom', 'left']).optional(),
  fromOffset: z.number().min(0).max(1).optional(),
  toOffset: z.number().min(0).max(1).optional(),
});

type SequenceFlowProps = z.infer<typeof sequenceFlowProps>;

const normalizeRoutingType = (value: unknown): SequenceFlowProps['routingType'] => {
  if (value === 'straight' || value === 'orthogonal' || value === 'orthogonalRounded' || value === 'bezier') return value;
  // Some pipelines may persist Konva render-type-like values; normalize them.
  if (value === 'orthogonalRoundedPolyline') return 'orthogonalRounded';
  if (value === 'orthogonalPolyline') return 'orthogonal';
  return 'orthogonalRounded';
};

const pointAtPolyline = (points: number[], t: number): { x: number; y: number } => {
  if (!points || points.length < 4) return { x: 0, y: 0 };
  const clamped = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0.5));

  let total = 0;
  const segLens: number[] = [];
  for (let i = 0; i < points.length - 2; i += 2) {
    const x1 = points[i] as number;
    const y1 = points[i + 1] as number;
    const x2 = points[i + 2] as number;
    const y2 = points[i + 3] as number;
    const len = Math.hypot(x2 - x1, y2 - y1);
    segLens.push(len);
    total += len;
  }
  if (!total) return { x: points[0] as number, y: points[1] as number };

  let dist = clamped * total;
  for (let i = 0; i < segLens.length; i++) {
    const len = segLens[i] as number;
    if (dist <= len) {
      const x1 = points[i * 2] as number;
      const y1 = points[i * 2 + 1] as number;
      const x2 = points[i * 2 + 2] as number;
      const y2 = points[i * 2 + 3] as number;
      const u = len ? dist / len : 0;
      return { x: x1 + (x2 - x1) * u, y: y1 + (y2 - y1) * u };
    }
    dist -= len;
  }

  return { x: points[points.length - 2] as number, y: points[points.length - 1] as number };
};

/**
 * Helper function to generate SVG path from points
 */
function polylineD(points: number[], routingType?: string): string {
  if (!points || points.length < 4) return '';

  // For bezier curves, we need at least 4 points (start, c1, c2, end)
  if (routingType === 'bezier' && points.length >= 8) {
    let d = `M ${points[0]} ${points[1]}`;
    // Use cubic bezier curve: C c1x,c1y c2x,c2y endx,endy
    d += ` C ${points[2]} ${points[3]} ${points[4]} ${points[5]} ${points[6]} ${points[7]}`;
    return d;
  }

  // For straight lines or orthogonal routing
  const rounded = routingType === 'orthogonalRounded';
  const rBase = 10;

  let d = `M ${points[0]} ${points[1]}`;
  for (let i = 2; i < points.length; i += 2) {
    const x = points[i] as number;
    const y = points[i + 1] as number;
    if (!rounded || i >= points.length - 2 || i < 2) {
      d += ` L ${x} ${y}`;
      continue;
    }

    const px = points[i - 2] as number;
    const py = points[i - 1] as number;
    const nx = points[i + 2] as number;
    const ny = points[i + 3] as number;

    const v1x = x - px;
    const v1y = y - py;
    const v2x = nx - x;
    const v2y = ny - y;
    const len1 = Math.hypot(v1x, v1y);
    const len2 = Math.hypot(v2x, v2y);

    const dot = v1x * v2x + v1y * v2y;
    const isCorner = len1 > 0 && len2 > 0 && Math.abs(dot) < 1e-6;
    if (!isCorner) {
      d += ` L ${x} ${y}`;
      continue;
    }

    const r = Math.min(rBase, len1 / 2, len2 / 2);
    const u1x = v1x / len1;
    const u1y = v1y / len1;
    const u2x = v2x / len2;
    const u2y = v2y / len2;

    const x1 = x - u1x * r;
    const y1 = y - u1y * r;
    const x2 = x + u2x * r;
    const y2 = y + u2y * r;

    d += ` L ${x1} ${y1} Q ${x} ${y} ${x2} ${y2}`;
  }
  return d;
}

/**
 * Helper function to generate SVG arrow marker
 */
function svgArrowMarker(id: string, color: string, size: number, type: string = 'triangle'): string {
  const scale = 0.6;
  const w = size * scale;
  const h = size * scale;

  switch (type) {
    case 'circle':
      return `<marker id="${id}" markerWidth="${w}" markerHeight="${h}" refX="${w / 2}" refY="${h / 2}" orient="auto" markerUnits="strokeWidth"><circle cx="${w / 2}" cy="${h / 2}" r="${w / 2}" fill="${color}"/></marker>`;
    case 'circleOutline':
      return `<marker id="${id}" markerWidth="${w}" markerHeight="${h}" refX="${w / 2}" refY="${h / 2}" orient="auto" markerUnits="strokeWidth"><circle cx="${w / 2}" cy="${h / 2}" r="${w / 2}" fill="#ffffff" stroke="${color}" stroke-width="1"/></marker>`;
    case 'diamond':
      return `<marker id="${id}" markerWidth="${w}" markerHeight="${h}" refX="${w}" refY="${h / 2}" orient="auto" markerUnits="strokeWidth"><path d="M${w},${h / 2} L${w / 2},0 L0,${h / 2} L${w / 2},${h} Z" fill="${color}"/></marker>`;
    case 'bar':
      return `<marker id="${id}" markerWidth="${w}" markerHeight="${h}" refX="${w}" refY="${h / 2}" orient="auto" markerUnits="strokeWidth"><line x1="${w / 2}" y1="0" x2="${w / 2}" y2="${h}" stroke="${color}" stroke-width="1"/></marker>`;
    case 'outlineBlock':
      return `<marker id="${id}" markerWidth="${w}" markerHeight="${h}" refX="${w}" refY="${h / 2}" orient="auto" markerUnits="strokeWidth"><path d="M0,0 L${w},${h / 2} L0,${h} Z" fill="#ffffff" stroke="${color}" stroke-width="1"/></marker>`;
    case 'chevron':
      return `<marker id="${id}" markerWidth="${w}" markerHeight="${h}" refX="${w}" refY="${h / 2}" orient="auto" markerUnits="strokeWidth"><path d="M0,0 L${w},${h / 2} L0,${h}" fill="none" stroke="${color}" stroke-width="1"/></marker>`;
    case 'backslash':
      return `<marker id="${id}" markerWidth="${w}" markerHeight="${h}" refX="${w / 2}" refY="${h / 2}" orient="auto" markerUnits="strokeWidth"><line x1="${w}" y1="0" x2="0" y2="${h}" stroke="${color}" stroke-width="2"/></marker>`;
    case 'triangle':
    default:
      return `<marker id="${id}" markerWidth="${w}" markerHeight="${h}" refX="${w}" refY="${h / 2}" orient="auto" markerUnits="strokeWidth"><path d="M0,0 L${w},${h / 2} L0,${h} Z" fill="${color}"/></marker>`;
  }
}

/**
 * Sequence Flow Connector Manifest
 * 
 * Represents the standard flow between flowchart nodes (process steps, decisions, etc.)
 * Defaults to orthogonal routing with a triangle arrowhead.
 */
export const SequenceFlowConnectorManifest: ConnectorManifest<SequenceFlowProps> = {
  id: 'sequence_flow',
  version: '1.0.0',
  category: 'flowchart',
  routingPolicyId: CORE_ROUTING_POLICY_IDS.processFlow,
  label: 'Sequence Flow',
  iconId: 'sequence-flow',
  propsSchema: sequenceFlowProps,
  defaultProps: () => ({
    color: '#334155',
    width: 2,
    dashed: false,
    endArrow: { type: 'triangle', size: 8 },
    startArrow: { type: 'none', size: 6 },
    routingType: 'orthogonalRounded',
  }),
  ui: {
    group: 'routing',
    collapsedByDefault: false,
  },
  svg: ({ points, props }) => {
    if (!points || points.length < 4) return '';
    const color = props.color || '#334155';
    const width = props.width || 2;
    const dash = props.dash
      ? props.dash.join(',')
      : props.dashed
        ? '6,6'
        : undefined;
    const routingType = normalizeRoutingType(props.routingType);

    const markers: string[] = [];
    let markerStart = '';
    let markerEnd = '';

    const exportConnectorIdRaw = (props as any).__exportConnectorId;
    const exportConnectorId =
      typeof exportConnectorIdRaw === 'string' && exportConnectorIdRaw.trim().length > 0
        ? exportConnectorIdRaw.trim().replace(/[^a-zA-Z0-9_-]+/g, '_')
        : '';

    if (props.startArrow?.type && props.startArrow.type !== 'none') {
      const id = exportConnectorId
        ? `arrow-start-${exportConnectorId}`
        : `arrow-start-${Math.abs(Math.round(points[0]))}-${Math.abs(Math.round(points[1]))}`;
      markers.push(svgArrowMarker(id, color, props.startArrow.size || 6, props.startArrow.type));
      markerStart = ` marker-start="url(#${id})"`;
    }

    if (props.endArrow?.type && props.endArrow.type !== 'none') {
      const id = exportConnectorId
        ? `arrow-end-${exportConnectorId}`
        : `arrow-end-${Math.abs(Math.round(points[points.length - 2]))}-${Math.abs(Math.round(points[points.length - 1]))}`;
      markers.push(svgArrowMarker(id, color, props.endArrow.size || 8, props.endArrow.type));
      markerEnd = ` marker-end="url(#${id})"`;
    }

    const defs = markers.length ? `<defs>${markers.join('')}</defs>` : '';
    const path = polylineD(points, routingType);

    const propsRecord = props as Record<string, unknown>;
    const labelText = typeof props.label === 'string' ? props.label.trim() : '';
    const connectorBody = `<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ''}${markerStart}${markerEnd} />`;
    if (!labelText) {
      return `${defs}${connectorBody}`;
    }

    const labelOffsetRaw = typeof propsRecord.labelOffset === 'number' ? propsRecord.labelOffset : 0.5;
    const labelOffset = Math.max(0, Math.min(1, Number.isFinite(labelOffsetRaw) ? labelOffsetRaw : 0.5));
    const mid = pointAtPolyline(points, labelOffset);
    const routingTypeNormalized = normalizeRoutingType(props.routingType);
    const labeled = finalizeConnectorExportWithLabel(defs, connectorBody, {
      x: mid.x,
      y: mid.y,
      text: labelText,
      strokeWidth: width,
      isBezier: routingTypeNormalized === 'bezier',
      props: propsRecord,
      connectorId: exportConnectorId || undefined,
    });
    propsRecord.__exportLabelSvg = labeled.labelSvg;
    propsRecord.__exportLabelMaskBounds = labeled.bounds;
    propsRecord.__exportLabelHandled = true;
    return labeled.geometrySvg;
  },
};

/**
 * Flowchart Line Connector Manifest
 *
 * Generic line connector used in flowchart diagrams (e.g. "Line" shape), but should still
 * respect routing + arrowheads + labels for exports.
 */
export const FlowchartLineConnectorManifest: ConnectorManifest<SequenceFlowProps> = {
  ...SequenceFlowConnectorManifest,
  id: 'line',
  label: 'Line',
  iconId: 'line',
  defaultProps: () => ({
    color: '#334155',
    width: 2,
    dashed: false,
    endArrow: { type: 'triangle', size: 8 },
    startArrow: { type: 'none', size: 6 },
    routingType: 'orthogonalRounded',
  }),
};

/**
 * Flowchart Connector Pack
 * 
 * Contains all flowchart-specific connector manifests
 */
export const FlowchartConnectorPack: ConnectorPack = {
  meta: {
    id: 'flowchart-connectors',
    name: 'Flowchart Connectors',
    version: '1.0.0',
  },
  diagramType: 'flowchart',
  connectors: [SequenceFlowConnectorManifest as ConnectorManifest<unknown>],
};

/**
 * Register all flowchart connectors
 * 
 * Call this function during app initialization to register flowchart connector manifests
 */
export function registerFlowchartConnectors(): void {
  registerDomainProfile(ProcessProfile);
  registerConnector('flowchart', SequenceFlowConnectorManifest);
  registerConnector('flowchart', FlowchartLineConnectorManifest);
}
